import React from "react";

export interface SegmentedItem<T extends string = string> {
  id: T;
  label: string;
}

export interface SegmentedControlProps<T extends string> {
  items: SegmentedItem<T>[];
  value: T;
  onChange: (id: T) => void;
  /** Names the group to a screen reader — "Plan kind", "Chart range". */
  label: string;
  className?: string;
}

/**
 * A pill row that switches between lists of the same thing — Meal, Supplement
 * and Exercise on a client's plans.
 *
 * Not `Tabs`. That one is the underlined row a screen's own sections hang off,
 * and it is already in use directly above this on the client page: a second
 * underlined row under the first reads as two levels of the same navigation
 * with nothing to tell them apart. These are three views of one list, so they
 * are drawn as a switch rather than as navigation.
 *
 * Generic over the id for the same reason `Tabs` is — each screen keeps its own
 * union of names instead of widening to `string`.
 */
function SegmentedControl<T extends string>({
  items,
  value,
  onChange,
  label,
  className = "",
}: SegmentedControlProps<T>) {
  return (
    <div
      role="tablist"
      aria-label={label}
      // `w-fit`, so the track hugs its pills rather than stretching across
      // whatever column it was dropped into.
      className={`inline-flex w-fit items-center gap-1 rounded-full border border-line bg-white p-1 ${className}`}
    >
      {items.map((item) => {
        const active = item.id === value;
        return (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(item.id)}
            className={`rounded-full px-4 sm:px-5 py-1.5 text-[13px] 3xl:text-[15px] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40 ${
              active
                ? "bg-[#BFF4FB] font-medium text-[#007E8C]"
                : "text-ink-muted hover:text-ink"
            }`}
          >
            {item.label}
          </button>
        );
      })}
    </div>
  );
}

export default SegmentedControl;
